import { gyromagneticRatio } from 'gyromagnetic-ratio';
import { isAnyArray } from 'is-any-array';
import { getDigitalFilterParameters } from './getDigitalFilterParameters';
import { getNucleusFromMetadata } from './getNucleusFromMetadata';
import { getSpectrumType } from './getSpectrumType';
export function getInfoFromMeta(metaData, options = {}) {
    const { subfix = '', logger } = options;
    const info = {
        dimension: 0,
        nucleus: [],
        isFid: false,
        isFt: false,
    };
    const metadataString = JSON.stringify(metaData);
    const separator = /\r\n/.exec(metadataString) ? '\r\n' : '\n';
    const { JCAMPDX: jcampdx = '', ORIGIN: origin = '' } = metaData;
    let creator = String(jcampdx).toLowerCase() + String(origin).toLowerCase();
    if (creator.includes('mestre') || creator.includes('nova')) {
        creator = 'mnova';
    }
    if (creator === 'mnova') {
        if (metaData.LONGDATE) {
            info.date = metaData.LONGDATE;
        }
    }
    maybeAdd(info, 'pulseSequence', metaData['.PULSESEQUENCE'] ||
        metaData['.PULPROG'] ||
        metaData[`${subfix}PULPROG`]);
    maybeAdd(info, 'aqMod', metaData[`${subfix}AQ_mod`]);
    maybeAdd(info, 'experiment', getSpectrumType(info, metaData, { subfix }));
    info.nucleus = getNucleusFromMetadata(metaData, info, subfix);
    info.dimension = info.nucleus.length;
    maybeAdd(info, 'fnMode', isAnyArray(metaData[`${subfix}FnMODE`])
        ? metaData[`${subfix}FnMODE`].map(getFnModeName)
        : getFnModeName(metaData[`${subfix}FnMODE`]));
    maybeAdd(info, 'title', metaData.TITLE);
    maybeAdd(info, 'solvent', metaData['.SOLVENTNAME']);
    maybeAdd(info, 'temperature', metaData[`${subfix}TE`] || metaData['.TE']);
    maybeAdd(info, 'type', metaData.DATATYPE);
    maybeAdd(info, 'dataClass', metaData[`${subfix}DATACLASS`]);
    maybeAdd(info, 'phc1', metaData[`${subfix}PHC1`]);
    maybeAdd(info, 'phc0', metaData[`${subfix}PHC0`]);
    if (info.type) {
        const typeUpperCase = info.type[0].toUpperCase();
        if (typeUpperCase.indexOf('FID') >= 0) {
            info.isFid = true;
            info.isComplex = true;
        }
        else if (typeUpperCase.indexOf('SPECTRUM') >= 0) {
            info.isFt = true;
        }
    }
    maybeAdd(info, 'firstX', metaData[`${subfix}FIRSTX`]);
    maybeAdd(info, 'lastX', metaData[`${subfix}LASTX`]);
    maybeAdd(info, 'numberOfPoints', metaData[`${subfix}NPOINTS`]);
    maybeAddNumber(info, 'originFrequency', metaData['.OBSERVEFREQUENCY']);
    maybeAdd(info, 'originFrequency', metaData[`${subfix}SFO1`]);
    if (!info.originFrequency ||
        info.originFrequency.some((frequency) => isNaN(frequency) || frequency === 0)) {
        if (!info.originFrequency) {
            info.originFrequency = info.dimension > 1 ? [400, 400] : [400];
        }
        info.originFrequency = info.originFrequency.map((frequency) => {
            return isNaN(frequency) || frequency === 0 ? 400 : frequency;
        });
        logger?.warn('The frequency could not be determined and was set to 400MHz');
    }
    if (creator !== 'mnova' && creator !== 'mestre') {
        const nucleus = info.nucleus[0];
        const gyromagneticRatioConst = gyromagneticRatio[nucleus];
        maybeAdd(info, 'probeName', metaData[`${subfix}PROBHD`]);
        maybeAdd(info, 'baseFrequency', metaData[`${subfix}BF1`]);
        if (!('baseFrequency' in info) && 'originFrequency' in info) {
            maybeAdd(info, 'baseFrequency', info.originFrequency);
        }
        if (!['baseFrequency', 'originFrequency'].some((e) => !info[e])) {
            const { baseFrequency } = info;
            const fieldStrength = 2 * Math.PI * (baseFrequency[0] / gyromagneticRatioConst) * 1e6;
            maybeAdd(info, 'fieldStrength', fieldStrength);
        }
        maybeAddNumber(info, 'spectralWidth', metaData[`${subfix}SW`]);
        maybeAddNumber(info, 'spectralWidth', metaData[`${subfix}QM_SPECTRAL_WIDTH`]);
        maybeAdd(info, 'numberOfPoints', metaData[`${subfix}TD`]);
        const numberOfPoints = info.numberOfPoints;
        maybeAdd(info, 'sampleName', metaData[`${subfix}NAME`]);
        if (metaData[`${subfix}FNTYPE`] !== undefined) {
            maybeAdd(info, 'acquisitionMode', parseInt(metaData[`${subfix}FNTYPE`], 10));
        }
        const varName = metaData[`${subfix}VARNAME`]
            ? metaData[`${subfix}VARNAME`].split(',')[0]
            : '';
        if (varName === 'TIME') {
            const value = typeof metaData.LAST === 'string' || metaData.LAST instanceof String
                ? metaData.LAST.replace(' ', '').split(',')[0]
                : metaData.LAST;
            maybeAddNumber(info, 'acquisitionTime', value);
        }
        if (!info.acquisitionTime) {
            if (!['numberOfPoints', 'spectralWidth'].some((e) => !info[e])) {
                const { spectralWidth, originFrequency } = info;
                maybeAdd(info, 'acquisitionTime', Number((numberOfPoints[0] - 1) /
                    (2 * spectralWidth[0] * originFrequency[0])));
            }
        }
        if (metaData[`${subfix}P`]) {
            const pulseStrength = 1e6 / (metaData[`${subfix}P`].split(separator)[1].split(' ')[1] * 4);
            maybeAdd(info, 'pulseStrength90', pulseStrength);
        }
        if (metaData[`${subfix}D`]) {
            const relaxationTime = metaData[`${subfix}D`]
                .split(separator)[1]
                .split(' ')[1];
            maybeAddNumber(info, 'relaxationTime', relaxationTime);
        }
        maybeAddNumber(info, 'numberOfScans', metaData[`${subfix}NS`]);
        maybeAddNumber(info, 'numberOfScans', metaData[`${subfix}QM_NSCANS`]);
        maybeAdd(info, 'groupDelay', metaData[`${subfix}GRPDLY`]);
        maybeAdd(info, 'DSPFVS', metaData[`${subfix}DSPFVS`]);
        maybeAdd(info, 'DECIM', metaData[`${subfix}DECIM`]);
        if (info.isFid &&
            ('groupDelay' in info || ['DSPFVS', 'DECIM'].every((e) => info[e]))) {
            const { groupDelay, DSPFVS, DECIM } = info;
            const digitalFilterParameters = getDigitalFilterParameters(groupDelay?.[0] ?? 0, DSPFVS?.[0], DECIM?.[0]);
            maybeAdd(info, 'digitalFilter', digitalFilterParameters);
        }
        let increment;
        if (['numberOfPoints', 'spectralWidth'].every((e) => info[e])) {
            const { spectralWidth, numberOfPoints } = info;
            if (info.isFid) {
                increment = numberOfPoints.map((nb) => {
                    return info.acquisitionTime[0] / (nb - 1);
                });
            }
            else {
                increment = numberOfPoints.map((nb, i) => {
                    return spectralWidth[i] / (nb - 1);
                });
            }
        }
        maybeAdd(info, 'increment', increment);
        if (metaData[`${subfix}DATE`]) {
            info.date = new Date(parseInt(metaData[`${subfix}DATE`], 10) * 1000).toISOString();
        }
        if (!info.solvent) {
            maybeAdd(info, 'solvent', Array.isArray(metaData[`${subfix}SOLVENT`])
                ? metaData[`${subfix}SOLVENT`][0]
                : metaData[`${subfix}SOLVENT`]);
        }
    }
    if (info.dimension < 2 &&
        'dataClass' in info &&
        info.dataClass[0].toUpperCase() === 'NTUPLES') {
        maybeAdd(info, 'last', metaData[`${subfix}LAST`].split(','));
        maybeAdd(info, 'first', metaData[`${subfix}FIRST`].split(','));
        maybeAdd(info, 'varDim', metaData[`${subfix}VARDIM`].split(','));
        maybeAdd(info, 'symbols', metaData[`${subfix}SYMBOL`].split(','));
        if (info.symbols) {
            if (info.symbols.includes('R') && info.symbols.includes('I')) {
                info.isComplex = true;
            }
            const index = info.symbols.findIndex((symbol) => symbol.toLowerCase() === 'x');
            maybeAddNumber(info, 'firstX', info.first[index]);
            maybeAddNumber(info, 'lastX', info.last[index]);
            maybeAddNumber(info, 'numberOfPoints', info.varDim[index]);
            if (!('spectralWidth' in info)) {
                const { lastX, numberOfPoints, firstX } = info;
                maybeAdd(info, 'spectralWidth', info.isFid
                    ? numberOfPoints / (2 * lastX)
                    : Math.abs(lastX - firstX) / info.originFrequency);
            }
        }
    }
    if (`${subfix}REVERSE` in metaData) {
        const tmp = metaData[`${subfix}REVERSE`];
        info.reverse = (Array.isArray(tmp) ? tmp : [tmp]).map((value) => value.includes('yes'));
    }
    if (!('frequencyOffset' in info) && metaData[`${subfix}REFERENCE_POINT`]) {
        const result = metaData[`${subfix}REFERENCE_POINT`].match(/(?<reference>[+-]?\d+(\.\d+)?)\s*.*/);
        if (result.groups?.reference !== undefined) {
            const { spectralWidth, baseFrequency } = info;
            maybeAdd(info, 'frequencyOffset', 0.5 * spectralWidth * baseFrequency - result.groups?.reference);
        }
    }
    if (!('frequencyOffset' in info) &&
        !['baseFrequency', 'originFrequency'].some((e) => !info[e])) {
        const { baseFrequency, originFrequency } = info;
        const frequencyOffset = baseFrequency.map((bf, i) => (originFrequency[i] - bf) * 1e6);
        maybeAdd(info, 'frequencyOffset', frequencyOffset);
    }
    for (const key in info) {
        if (info[key].length === 1)
            info[key] = info[key][0];
    }
    if (!info.frequencyOffset && '.SHIFTREFERENCE' in metaData) {
        processShiftReference(metaData, info);
    }
    if (!Array.isArray(info.nucleus))
        info.nucleus = [info.nucleus];
    return info;
}
export function maybeAdd(obj, name, value) {
    if (value === undefined)
        return;
    if (Array.isArray(value)) {
        obj[name] = value.map(cleanValue);
    }
    else {
        obj[name] = [cleanValue(value)];
    }
}
function maybeAddNumber(obj, name, value) {
    if (value === undefined)
        return;
    if (typeof value === 'string') {
        value = Number(value.replace(/\$.*/, ''));
    }
    maybeAdd(obj, name, value);
}
function cleanValue(value) {
    if (typeof value === 'string') {
        if (value.startsWith('<') && value.endsWith('>')) {
            value = value.substring(1, value.length - 1);
        }
        value = value.trim();
    }
    return value;
}
function processShiftReference(metadata, info) {
    const shiftReference = metadata['.SHIFTREFERENCE'];
    const regex = shiftReference.trim().startsWith('(')
        ? /\((?<data>[^)]+)\)/
        : /(?<data>[^)]+)/;
    const matchData = shiftReference.match(regex);
    if (matchData.groups.data) {
        const data = matchData.groups.data.split(',');
        if (data.length < 4) {
            return;
        }
        const numberOfPoints = parseInt(info.numberOfPoints, 10);
        const { spectralWidth, baseFrequency } = info;
        const point = parseInt(data[2], 10);
        const referencePpm = parseFloat(data[3]);
        let offset;
        if (info.isFid) {
            if (['baseFrequency', 'numberOfPoints', 'spectralWidth'].some((key) => !(key in info))) {
                return;
            }
            offset =
                referencePpm +
                    ((numberOfPoints * 0.5 - point) * spectralWidth) / numberOfPoints;
        }
        else {
            const firstX = parseFloat(info.firstX);
            const lastX = parseFloat(info.lastX);
            const center = (lastX + firstX) / 2;
            const deltaX = (lastX - firstX) / (numberOfPoints + 1);
            const currentValue = deltaX < 0 ? firstX : lastX + (point - 1) * deltaX;
            offset = (center - currentValue) / baseFrequency + referencePpm;
        }
        if (offset !== undefined && !isNaN(offset)) {
            info.frequencyOffset = offset * baseFrequency;
        }
    }
}
function getFnModeName(fnMode) {
    const fnModeNumber = typeof fnMode === 'string' ? parseInt(fnMode, 10) : fnMode;
    switch (fnModeNumber) {
        case 1:
            return 'QF';
        case 2:
            return 'QSEQ';
        case 3:
            return 'TTPI';
        case 4:
            return 'States';
        case 5:
            return 'States-TTPI';
        case 6:
            return 'Echo-antiecho';
        default:
            return 'undefined';
    }
}
//# sourceMappingURL=getInfoFromMeta.js.map